import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building, Plus, Search, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate } from '@/lib/utils';

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  plan: string;
  billingRegion: string;
  billingCurrency: string;
  isActive: boolean;
  gxpValidated: boolean;
  maxSeats: number;
  createdAt: string;
  _count: { users: number; documents: number };
}

export function PlatformTenantsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const tenants = useQuery({
    queryKey: ['platform-admin', 'tenants', q],
    queryFn: async () => (await api.get(`/api/platform-admin/tenants?q=${encodeURIComponent(q)}`)).data,
  });

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post('/api/platform-admin/tenants', body)).data,
    onSuccess: () => {
      toast.success('Tenant created', 'Admin invitation email will be sent if SMTP is configured.');
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: ['platform-admin', 'tenants'] });
      qc.invalidateQueries({ queryKey: ['platform-admin', 'metrics'] });
    },
    onError: (err) => toast.error('Create failed', extractApiError(err).message),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl">Tenants</h1>
          <p className="text-sm text-muted-foreground">Organisations on the Praxara platform.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> New tenant
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tenants..." className="pl-8" />
      </div>

      {tenants.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-36" />)}
        </div>
      ) : !tenants.data?.items?.length ? (
        <EmptyState icon={Building} title="No tenants yet" description="Create the first Praxara customer tenant." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {tenants.data.items.map((t: TenantRow) => (
            <Link
              key={t.id}
              to={`/platform-admin/tenants/${t.id}`}
              className="block rounded-lg border bg-card p-4 transition-all hover:border-primary/40 hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{t.name}</div>
                    {t.gxpValidated && <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    <code>{t.slug}</code> ? {t.billingRegion} · {t.billingCurrency}
                  </div>
                </div>
                <Badge variant={t.isActive ? 'success' : 'destructive'} className="shrink-0">
                  {t.isActive ? (
                    <><CheckCircle2 className="mr-1 h-3 w-3" /> Active</>
                  ) : (
                    <><XCircle className="mr-1 h-3 w-3" /> Inactive</>
                  )}
                </Badge>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-3 text-xs">
                <Mini label="Plan" value={t.plan.toLowerCase()} />
                <Mini label="Users" value={t._count.users.toString()} />
                <Mini label="Documents" value={t._count.documents.toString()} />
              </div>
              <div className="mt-2 text-[10px] text-muted-foreground">Created {formatDate(t.createdAt)}</div>
            </Link>
          ))}
        </div>
      )}

      {createOpen && <CreateTenantDialog onClose={() => setCreateOpen(false)} onSubmit={(b) => create.mutate(b)} busy={create.isPending} />}
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-medium capitalize">{value}</div>
    </div>
  );
}

function CreateTenantDialog({
  onClose,
  onSubmit,
  busy,
}: {
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const [form, setForm] = useState({
    name: '',
    slug: '',
    plan: 'starter' as 'starter' | 'professional' | 'enterprise',
    billingRegion: 'UK' as 'UK' | 'EU' | 'US',
    billingCurrency: 'GBP' as 'GBP' | 'EUR' | 'USD',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    adminPassword: '',
    maxSeats: 10,
    gxpValidated: false,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onSubmit(form);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New tenant</DialogTitle>
          <DialogDescription>
            Provision a Praxara customer tenant + initial tenant ADMIN account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Organisation name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                required
                className="font-mono"
              />
            </div>
            <div>
              <Label>Plan</Label>
              <select
                value={form.plan}
                onChange={(e) => setForm({ ...form, plan: e.target.value as typeof form.plan })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <Label>Max seats</Label>
              <Input
                type="number"
                value={form.maxSeats}
                onChange={(e) => setForm({ ...form, maxSeats: Math.max(1, Number(e.target.value) || 1) })}
                min={1}
              />
            </div>
            <div>
              <Label>Region</Label>
              <select
                value={form.billingRegion}
                onChange={(e) => {
                  const r = e.target.value as 'UK' | 'EU' | 'US';
                  setForm({ ...form, billingRegion: r, billingCurrency: r === 'UK' ? 'GBP' : r === 'EU' ? 'EUR' : 'USD' });
                }}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="UK">UK</option>
                <option value="EU">EU</option>
                <option value="US">US</option>
              </select>
            </div>
            <div>
              <Label>Currency</Label>
              <select
                value={form.billingCurrency}
                onChange={(e) => setForm({ ...form, billingCurrency: e.target.value as typeof form.billingCurrency })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="rounded-md border bg-muted/30 p-3">
            <div className="mb-2 text-xs font-medium">Initial ADMIN user</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First name</Label>
                <Input value={form.adminFirstName} onChange={(e) => setForm({ ...form, adminFirstName: e.target.value })} required />
              </div>
              <div>
                <Label>Last name</Label>
                <Input value={form.adminLastName} onChange={(e) => setForm({ ...form, adminLastName: e.target.value })} required />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input type="email" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} required />
              </div>
              <div className="col-span-2">
                <Label>Initial password</Label>
                <Input
                  type="text"
                  value={form.adminPassword}
                  onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
                  required
                  minLength={12}
                  placeholder=">=12 characters"
                />
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Admin will be prompted to change on first login.
                </p>
              </div>
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.gxpValidated}
              onChange={(e) => setForm({ ...form, gxpValidated: e.target.checked })}
              className="h-4 w-4 accent-primary"
            />
            Mark as GxP validated (IQ/OQ/PQ complete)
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Creating...' : 'Create tenant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ensure Card import is not removed by TS — CardContent is re-used via list cards
void Card;
void CardContent;
